import {
  buildAiStudioImageOutputRows,
  buildAiStudioImageSuccessResult,
  createAiStudioImageTask,
  markAiStudioImageTaskFailed,
  markAiStudioImageTaskSuccess,
} from './aiStudioImageTaskRecord';
import { allocateTaskIdentifiersLocal } from './taskIdGenerator';
import { taskAPI } from '@/utils/api';

async function rememberAwaitingFundingTask(workflowContext, initialResult, taskId) {
  const wfId = String(workflowContext?.workflowTaskId || initialResult?.workflowTaskId || '').trim();
  if (!wfId) return;
  const { setWorkflowAwaitingFundingTask } = await import('@/pages/ipe/workbench/constants/workflowTaskQueue');
  setWorkflowAwaitingFundingTask(wfId, taskId);
}

async function clearAwaitingFundingTask(initialResult) {
  const wfId = String(initialResult?.workflowTaskId || '').trim();
  if (!wfId) return;
  const { setWorkflowAwaitingFundingTask } = await import('@/pages/ipe/workbench/constants/workflowTaskQueue');
  setWorkflowAwaitingFundingTask(wfId, null);
}

export function buildWorkflowFeatureMeta(workflowContext = {}) {
  const {
    workflowRunId,
    row,
    workflowTaskId,
    templateName,
    operatorName,
  } = workflowContext;
  return {
    workflowBatch: true,
    source: 'workflow',
    ...(workflowRunId != null ? { workflowRunId } : {}),
    ...(row?.id != null ? { workflowRowId: row.id } : {}),
    ...(workflowTaskId ? { workflowTaskId } : {}),
    ...(templateName ? { workflowTemplateName: templateName } : {}),
    ...(operatorName ? { operator: operatorName, operatorName } : {}),
  };
}

export function mergeWorkflowFeatureMeta(initialResult, workflowContext = {}) {
  return {
    ...initialResult,
    ...buildWorkflowFeatureMeta(workflowContext),
  };
}

export async function beginWorkflowFeatureTask({
  taskType,
  taskName,
  imageIds = [],
  previewUrl = '',
  initialResult = {},
  estimatedTotal = 1,
  workflowContext = {},
}) {
  const { taskId, subAccount } = allocateTaskIdentifiersLocal(taskType);
  const mergedResult = mergeWorkflowFeatureMeta(initialResult, workflowContext);
  await createAiStudioImageTask({
    taskType,
    taskName,
    taskId,
    subAccount,
    imageIds,
    previewUrl: previewUrl || mergedResult.previewUrl || '',
    initialResult: mergedResult,
    estimatedTotal,
  });
  if (mergedResult.serverWorker === true) {
    await rememberAwaitingFundingTask(workflowContext, mergedResult, taskId);
  }
  return { taskId, subAccount, initialResult: mergedResult };
}

export async function completeWorkflowFeatureImageTask(
  taskId,
  urls,
  estimatedTotal,
  initialResult,
  extraResult = {},
  rawApiResult = {},
  navId = '',
) {
  const normalizedUrls = (Array.isArray(urls) ? urls : []).filter(Boolean);
  const successResult = buildAiStudioImageSuccessResult(
    { ...initialResult, ...extraResult },
    {
      ...rawApiResult,
      urls: normalizedUrls,
      outputs: rawApiResult.outputs || buildAiStudioImageOutputRows(normalizedUrls),
    },
    normalizedUrls,
  );
  const taskType = String(initialResult?.taskType || extraResult?.taskType || navId || '').trim();
  const marked = await markAiStudioImageTaskSuccess(
    taskId,
    normalizedUrls,
    estimatedTotal,
    successResult,
    taskType ? { navId: taskType } : null,
  );
  await clearAwaitingFundingTask(initialResult);
  return marked;
}

export async function failWorkflowFeatureTask(taskId, initialResult, estimatedTotal, error) {
  let existingResult = {};
  try {
    const res = await taskAPI.getById(taskId);
    const task = res?.task || res;
    const st = String(task?.status || '').toLowerCase();
    existingResult = task?.result && typeof task.result === 'object' ? task.result : {};
    if (['failed', 'success', 'partial', 'canceled', 'cancelled'].includes(st)) {
      await clearAwaitingFundingTask({ ...initialResult, ...existingResult });
      return;
    }
  } catch {
    /* 读失败仍按失败落库 */
  }
  const existingHint = String(existingResult.failureHint || existingResult.error || '').trim();
  await markAiStudioImageTaskFailed(
    taskId,
    {
      ...initialResult,
      ...existingResult,
      ...(existingHint ? { failureHint: existingHint } : {}),
    },
    estimatedTotal,
    error,
  );
  await clearAwaitingFundingTask({ ...initialResult, ...existingResult });
}
